import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  CheckCircle2 as SuccessIcon,
  AlertCircle as ErrorIcon
} from 'lucide-react';
import api from '../utils/axios';

const EmailVerification = () => {
  const { uidb64, token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!uidb64 || !token) {
        setError('Invalid verification link');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/accounts/verify-email/${uidb64}/${token}/`);
        setSuccess(true);
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || 'Email verification failed');
        setSuccess(false);
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [uidb64, token]);

  const handleContinue = () => {
    navigate('/login');
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        {loading ? (
          <Box>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              Verifying your email...
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we verify your email address.
            </Typography>
          </Box>
        ) : success ? (
          <Box>
            <SuccessIcon size={60} color="var(--ab-approved)" />
            <Typography variant="h4" gutterBottom color="success.main">
              Email Verified!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your email address has been successfully verified. You can now log in to your account.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleContinue}
              sx={{ mt: 2 }}
            >
              Continue to Login
            </Button>
          </Box>
        ) : (
          <Box>
            <ErrorIcon size={60} color="var(--ab-denied)" />
            <Typography variant="h4" gutterBottom color="error.main">
              Verification Failed
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Typography variant="body1" sx={{ mb: 3 }}>
              The verification link may be invalid or expired. You can request a new verification email.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/signup')}
              >
                Sign Up Again
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default EmailVerification;
